import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Search, Edit, Trash2, Package, Users, FileText, Calendar, Filter } from 'lucide-react';
import { toast } from 'sonner';

const Inventory = () => {
  const [activeTab, setActiveTab] = useState('purchases'); // 'purchases' or 'suppliers'
  const [purchases, setPurchases] = useState([]);
  const [filteredPurchases, setFilteredPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'week', 'month', 'custom'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Modals
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isSupplierDialogOpen, setIsSupplierDialogOpen] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // User role
  const [userRole, setUserRole] = useState('');

  // Forms data
  const [purchaseFormData, setPurchaseFormData] = useState({
    invoice_number: '',
    product_id: '',
    supplier_id: '',
    purchase_price: '',
    quantity: '',
    purchase_date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  const [supplierFormData, setSupplierFormData] = useState({
    name: '',
    phone: '',
    address: '',
    email: ''
  });

  useEffect(() => {
    fetchPurchases();
    fetchSuppliers();
    fetchProducts();
    
    // Get user role
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setUserRole(user.role);
      } catch (error) {
        console.error('Error parsing user:', error);
      }
    }
  }, []);

  useEffect(() => {
    applyFilters();
  }, [purchases, searchQuery, dateFilter, startDate, endDate]);

  const fetchPurchases = async () => {
    try {
      const response = await axios.get('/purchases');
      setPurchases(response.data);
    } catch (error) {
      toast.error('Error al cargar compras');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get('/suppliers');
      setSuppliers(response.data);
    } catch (error) {
      toast.error('Error al cargar proveedores');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  };

  const applyFilters = () => {
    let filtered = [...purchases];

    // Date filter
    const now = new Date();
    if (dateFilter === 'today') {
      const today = now.toISOString().split('T')[0];
      filtered = filtered.filter(p => p.purchase_date === today);
    } else if (dateFilter === 'week') {
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(p => new Date(p.purchase_date) >= weekAgo);
    } else if (dateFilter === 'month') {
      const monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      filtered = filtered.filter(p => new Date(p.purchase_date) >= monthAgo);
    } else if (dateFilter === 'custom' && startDate && endDate) {
      filtered = filtered.filter(p => 
        p.purchase_date >= startDate && p.purchase_date <= endDate
      );
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.invoice_number.toLowerCase().includes(query) ||
        p.product_name.toLowerCase().includes(query) ||
        p.supplier_name.toLowerCase().includes(query)
      );
    }

    setFilteredPurchases(filtered);
  };

  const handleSubmitPurchase = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = {
        ...purchaseFormData,
        purchase_price: parseFloat(purchaseFormData.purchase_price),
        quantity: parseInt(purchaseFormData.quantity)
      };

      if (editingPurchase) {
        await axios.put(`/purchases/${editingPurchase.id}`, data);
        toast.success('Compra actualizada exitosamente');
      } else {
        await axios.post('/purchases', data);
        toast.success('Compra registrada exitosamente');
      }

      resetPurchaseForm();
      fetchPurchases();
      fetchProducts(); // Refresh products to update stock
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar compra');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitSupplier = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingSupplier) {
        await axios.put(`/suppliers/${editingSupplier.id}`, supplierFormData);
        toast.success('Proveedor actualizado exitosamente');
      } else {
        await axios.post('/suppliers', supplierFormData);
        toast.success('Proveedor registrado exitosamente');
      }

      resetSupplierForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar proveedor');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPurchase = (purchase) => {
    setEditingPurchase(purchase);
    setPurchaseFormData({
      invoice_number: purchase.invoice_number,
      product_id: purchase.product_id,
      supplier_id: purchase.supplier_id,
      purchase_price: purchase.purchase_price.toString(),
      quantity: purchase.quantity.toString(),
      purchase_date: purchase.purchase_date,
      notes: purchase.notes || ''
    });
    setIsPurchaseDialogOpen(true);
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setSupplierFormData({
      name: supplier.name,
      phone: supplier.phone || '',
      address: supplier.address || '',
      email: supplier.email || ''
    });
    setIsSupplierDialogOpen(true);
  };

  const handleDeletePurchase = async (purchaseId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta compra? El stock será revertido.')) return;

    try {
      await axios.delete(`/purchases/${purchaseId}`);
      toast.success('Compra eliminada y stock revertido');
      fetchPurchases();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar compra');
    }
  };

  const handleDeleteSupplier = async (supplierId) => {
    if (!window.confirm('¿Estás seguro de eliminar este proveedor?')) return;

    try {
      await axios.delete(`/suppliers/${supplierId}`);
      toast.success('Proveedor eliminado exitosamente');
      fetchSuppliers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar proveedor');
    }
  };

  const resetPurchaseForm = () => {
    setPurchaseFormData({
      invoice_number: '',
      product_id: '',
      supplier_id: '',
      purchase_price: '',
      quantity: '',
      purchase_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setEditingPurchase(null);
    setIsPurchaseDialogOpen(false);
  };

  const resetSupplierForm = () => {
    setSupplierFormData({
      name: '',
      phone: '',
      address: '',
      email: ''
    });
    setEditingSupplier(null);
    setIsSupplierDialogOpen(false);
  };

  const calculateTotal = () => {
    const price = parseFloat(purchaseFormData.purchase_price) || 0;
    const qty = parseInt(purchaseFormData.quantity) || 0;
    return (price * qty).toFixed(2);
  };

  const getTotalPurchases = () => {
    return filteredPurchases.reduce((sum, p) => sum + p.total_amount, 0).toFixed(2);
  };

  return (
    <div className="inventory-page" data-testid="inventory-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Control de Inventario</h1>
          <p className="page-subtitle">Gestión de compras y proveedores</p>
        </div>
        {userRole === 'admin' && (
          <Button 
            onClick={() => activeTab === 'purchases' ? setIsPurchaseDialogOpen(true) : setIsSupplierDialogOpen(true)}
            data-testid="add-btn"
          >
            <Plus size={20} />
            {activeTab === 'purchases' ? 'Nueva Compra' : 'Nuevo Proveedor'}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'purchases' ? 'active' : ''}`}
          onClick={() => setActiveTab('purchases')}
        >
          <FileText size={16} />
          Compras
        </button>
        <button 
          className={`tab ${activeTab === 'suppliers' ? 'active' : ''}`}
          onClick={() => setActiveTab('suppliers')}
        >
          <Users size={16} />
          Proveedores
        </button>
      </div>

      {activeTab === 'purchases' ? (
        <>
          {/* Filters */}
          <Card className="filters-card">
            <div className="filters-content">
              <div className="filter-group">
                <Label>Buscar</Label>
                <div className="search-box">
                  <Search size={18} className="search-icon" />
                  <Input
                    type="text"
                    placeholder="Buscar por factura, producto o proveedor..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="filter-group">
                <Label>Período</Label>
                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    <SelectItem value="today">Hoy</SelectItem>
                    <SelectItem value="week">Última Semana</SelectItem>
                    <SelectItem value="month">Último Mes</SelectItem>
                    <SelectItem value="custom">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {dateFilter === 'custom' && (
                <>
                  <div className="filter-group">
                    <Label>Desde</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="filter-group">
                    <Label>Hasta</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="summary-stats">
              <div className="stat">
                <span className="stat-label">Total Compras:</span>
                <span className="stat-value">{filteredPurchases.length}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Monto Total:</span>
                <span className="stat-value total-amount">${getTotalPurchases()}</span>
              </div>
            </div>
          </Card>

          {/* Purchases List */}
          <div className="purchases-list">
            {filteredPurchases.length === 0 ? (
              <Card className="empty-state">
                <Package size={64} />
                <h3>No hay compras registradas</h3>
                <p>Las compras aparecerán aquí</p>
              </Card>
            ) : (
              filteredPurchases.map((purchase) => (
                <Card key={purchase.id} className="purchase-card">
                  <div className="purchase-header">
                    <div>
                      <h3 className="purchase-invoice">Factura: {purchase.invoice_number}</h3>
                      <p className="purchase-date">
                        {new Date(purchase.purchase_date).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="purchase-total">${purchase.total_amount.toFixed(2)}</div>
                  </div>

                  <div className="purchase-details">
                    <div className="detail-item">
                      <span className="detail-label">Producto:</span>
                      <span className="detail-value">{purchase.product_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Proveedor:</span>
                      <span className="detail-value">{purchase.supplier_name}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Cantidad:</span>
                      <span className="detail-value">{purchase.quantity} unidades</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Precio Unitario:</span>
                      <span className="detail-value">${purchase.purchase_price.toFixed(2)}</span>
                    </div>
                    {purchase.notes && (
                      <div className="detail-item full-width">
                        <span className="detail-label">Notas:</span>
                        <p className="detail-notes">{purchase.notes}</p>
                      </div>
                    )}
                  </div>

                  {userRole === 'admin' && (
                    <div className="purchase-actions">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditPurchase(purchase)}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeletePurchase(purchase.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </>
      ) : (
        /* Suppliers List */
        <div className="suppliers-grid">
          {suppliers.length === 0 ? (
            <Card className="empty-state">
              <Users size={64} />
              <h3>No hay proveedores registrados</h3>
              <p>Los proveedores aparecerán aquí</p>
            </Card>
          ) : (
            suppliers.map((supplier) => (
              <Card key={supplier.id} className="supplier-card">
                <div className="supplier-header">
                  <h3 className="supplier-name">{supplier.name}</h3>
                  {userRole === 'admin' && (
                    <div className="supplier-actions">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditSupplier(supplier)}
                      >
                        <Edit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteSupplier(supplier.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  )}
                </div>

                <div className="supplier-details">
                  {supplier.phone && (
                    <div className="supplier-info">
                      <span>📞 {supplier.phone}</span>
                    </div>
                  )}
                  {supplier.email && (
                    <div className="supplier-info">
                      <span>✉️ {supplier.email}</span>
                    </div>
                  )}
                  {supplier.address && (
                    <div className="supplier-info">
                      <span>📍 {supplier.address}</span>
                    </div>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      )}

      {/* Purchase Dialog */}
      <Dialog open={isPurchaseDialogOpen} onOpenChange={setIsPurchaseDialogOpen}>
        <DialogContent className="purchase-dialog">
          <DialogHeader>
            <DialogTitle>{editingPurchase ? 'Editar Compra' : 'Nueva Compra'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitPurchase} className="purchase-form">
            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="invoice_number">Número de Factura*</Label>
                <Input
                  id="invoice_number"
                  value={purchaseFormData.invoice_number}
                  onChange={(e) => setPurchaseFormData({...purchaseFormData, invoice_number: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <Label htmlFor="purchase_date">Fecha de Compra*</Label>
                <Input
                  id="purchase_date"
                  type="date"
                  value={purchaseFormData.purchase_date}
                  onChange={(e) => setPurchaseFormData({...purchaseFormData, purchase_date: e.target.value})}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <Label htmlFor="product_id">Producto*</Label>
              <Select 
                value={purchaseFormData.product_id} 
                onValueChange={(value) => setPurchaseFormData({...purchaseFormData, product_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un producto" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(product => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} (Stock actual: {product.stock})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label htmlFor="supplier_id">Proveedor*</Label>
              <Select 
                value={purchaseFormData.supplier_id} 
                onValueChange={(value) => setPurchaseFormData({...purchaseFormData, supplier_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona un proveedor" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map(supplier => (
                    <SelectItem key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <Label htmlFor="purchase_price">Precio de Compra*</Label>
                <Input
                  id="purchase_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={purchaseFormData.purchase_price}
                  onChange={(e) => setPurchaseFormData({...purchaseFormData, purchase_price: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <Label htmlFor="quantity">Cantidad*</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="1"
                  value={purchaseFormData.quantity}
                  onChange={(e) => setPurchaseFormData({...purchaseFormData, quantity: e.target.value})}
                  required
                />
              </div>
            </div>

            {purchaseFormData.purchase_price && purchaseFormData.quantity && (
              <div className="total-preview">
                <span>Total:</span>
                <span className="total-amount">${calculateTotal()}</span>
              </div>
            )}

            <div className="form-group">
              <Label htmlFor="notes">Notas</Label>
              <Textarea
                id="notes"
                value={purchaseFormData.notes}
                onChange={(e) => setPurchaseFormData({...purchaseFormData, notes: e.target.value})}
                rows={3}
                placeholder="Notas adicionales..."
              />
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={resetPurchaseForm}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Guardando...' : (editingPurchase ? 'Actualizar' : 'Guardar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Supplier Dialog */}
      <Dialog open={isSupplierDialogOpen} onOpenChange={setIsSupplierDialogOpen}>
        <DialogContent className="supplier-dialog">
          <DialogHeader>
            <DialogTitle>{editingSupplier ? 'Editar Proveedor' : 'Nuevo Proveedor'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmitSupplier} className="supplier-form">
            <div className="form-group">
              <Label htmlFor="name">Nombre*</Label>
              <Input
                id="name"
                value={supplierFormData.name}
                onChange={(e) => setSupplierFormData({...supplierFormData, name: e.target.value})}
                required
              />
            </div>

            <div className="form-group">
              <Label htmlFor="phone">Teléfono</Label>
              <Input
                id="phone"
                value={supplierFormData.phone}
                onChange={(e) => setSupplierFormData({...supplierFormData, phone: e.target.value})}
              />
            </div>

            <div className="form-group">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={supplierFormData.email}
                onChange={(e) => setSupplierFormData({...supplierFormData, email: e.target.value})}
              />
            </div>

            <div className="form-group">
              <Label htmlFor="address">Dirección</Label>
              <Textarea
                id="address"
                value={supplierFormData.address}
                onChange={(e) => setSupplierFormData({...supplierFormData, address: e.target.value})}
                rows={3}
              />
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={resetSupplierForm}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Guardando...' : (editingSupplier ? 'Actualizar' : 'Guardar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .inventory-page {
          max-width: 1400px;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #64748b;
        }

        .tabs {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 2rem;
          background: white;
          padding: 0.5rem;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .tab {
          flex: 1;
          padding: 0.875rem 1.5rem;
          border: none;
          background: transparent;
          border-radius: 8px;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .tab:hover {
          background: #f8fafc;
        }

        .tab.active {
          background: #3b82f6;
          color: white;
        }

        .filters-card {
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .filters-content {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          color: #94a3b8;
        }

        .search-box input {
          padding-left: 2.5rem;
        }

        .summary-stats {
          display: flex;
          gap: 2rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e2e8f0;
        }

        .stat {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .stat-label {
          font-size: 0.875rem;
          color: #64748b;
        }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        .stat-value.total-amount {
          color: #10b981;
        }

        .purchases-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .purchase-card {
          padding: 1.5rem;
        }

        .purchase-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .purchase-invoice {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .purchase-date {
          font-size: 0.875rem;
          color: #64748b;
        }

        .purchase-total {
          font-size: 1.5rem;
          font-weight: 700;
          color: #10b981;
        }

        .purchase-details {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .detail-item {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .detail-item.full-width {
          grid-column: 1 / -1;
        }

        .detail-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .detail-value {
          font-size: 0.9375rem;
          font-weight: 500;
          color: #1e293b;
        }

        .detail-notes {
          margin: 0;
          padding: 0.75rem;
          background: #f8fafc;
          border-radius: 6px;
          border-left: 3px solid #3b82f6;
          font-size: 0.875rem;
          color: #475569;
        }

        .purchase-actions {
          display: flex;
          gap: 0.5rem;
          justify-content: flex-end;
        }

        .suppliers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 1.5rem;
        }

        .supplier-card {
          padding: 1.5rem;
        }

        .supplier-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1rem;
          padding-bottom: 1rem;
          border-bottom: 1px solid #e2e8f0;
        }

        .supplier-name {
          font-size: 1.125rem;
          font-weight: 600;
          color: #1e293b;
        }

        .supplier-actions {
          display: flex;
          gap: 0.5rem;
        }

        .supplier-details {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .supplier-info {
          font-size: 0.9375rem;
          color: #475569;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          text-align: center;
          color: #94a3b8;
        }

        .empty-state h3 {
          font-size: 1.25rem;
          font-weight: 600;
          color: #64748b;
          margin: 1rem 0 0.5rem;
        }

        .purchase-dialog,
        .supplier-dialog {
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .purchase-form,
        .supplier-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          margin-top: 1rem;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .total-preview {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem;
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
        }

        .total-preview span:first-child {
          font-size: 1rem;
          font-weight: 600;
          color: #166534;
        }

        .total-preview .total-amount {
          font-size: 1.5rem;
          font-weight: 700;
          color: #10b981;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
          }

          .form-row {
            grid-template-columns: 1fr;
          }

          .filters-content {
            grid-template-columns: 1fr;
          }

          .summary-stats {
            flex-direction: column;
            gap: 1rem;
          }

          .purchase-header {
            flex-direction: column;
            gap: 0.5rem;
          }

          .purchase-details {
            grid-template-columns: 1fr;
          }

          .suppliers-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default Inventory;
